import {
  BOOK_VOUCHER_PRICE_DISPLAY,
  bookVoucherEligibleTemplates,
  bookVoucherStartsEdition,
  bookCreationProfileForTemplate,
  type CreationProductionFamily,
  type PrintFormat,
  type TemplateId,
} from "@lifescapsule/contracts";

export type EditionFamily = "legacy" | "relationships" | "playful" | "traditions";
export type PurchaserFit = "parent" | "partner" | "family" | "self";
export type CatalogAvailability = "WORKING" | "SANDBOX" | "ROADMAP";
export type EditionMotif =
  | "timeline"
  | "ribbon"
  | "compass"
  | "woven"
  | "moon"
  | "branches"
  | "map"
  | "horizon"
  | "letters"
  | "star"
  | "plate"
  | "panels";

export const familyOrder: EditionFamily[] = ["legacy", "relationships", "playful", "traditions"];

export const familyLabels: Record<EditionFamily, string> = {
  legacy: "Life & Legacy",
  relationships: "Relationships & Family",
  playful: "Playful Stories",
  traditions: "Traditions",
};

export const purchaserLabels: Record<PurchaserFit | "all", string> = {
  all: "Everyone",
  parent: "A parent or grandparent",
  partner: "My partner",
  family: "Our whole family",
  self: "Myself",
};

export const availabilityOrder: CatalogAvailability[] = ["WORKING", "SANDBOX", "ROADMAP"];

export const availabilityPresentation: Record<
  CatalogAvailability,
  { label: string; shortLabel: string; explanation: string }
> = {
  WORKING: {
    label: "Ready to begin",
    shortLabel: "Available",
    explanation:
      "Start your own private book and save your work as you go. If you have a voucher, check that it includes this book. You’ll review the finished pages before arranging a printed copy.",
  },
  SANDBOX: {
    label: "Guided experience",
    shortLabel: "Try it",
    explanation:
      "Follow the guided steps and save your private book as you go. You’ll review every page before anything is printed.",
  },
  ROADMAP: {
    label: "Coming soon",
    shortLabel: "Coming soon",
    explanation:
      "The edition is being prepared for customer use. You can explore its example while creation remains closed.",
  },
};

export const productionFamilyOrder: CreationProductionFamily[] = [
  "NARRATIVE",
  "PICTURE_BOOK",
  "RECIPE",
  "COMIC_CORE",
];

export const productionFamilyPresentation: Record<
  CreationProductionFamily,
  { eyebrow: string; label: string; description: string }
> = {
  NARRATIVE: {
    eyebrow: "Tell a life story",
    label: "Life stories",
    description:
      "Turn recordings, written memories and photos into chapters. Keep the original memories alongside the story so you can check every detail.",
  },
  PICTURE_BOOK: {
    eyebrow: "Make a story to read together",
    label: "Picture book",
    description:
      "Turn a memory into an illustrated story with eight page turns. Choose the words and pictures, check permissions for anyone shown, then read the book aloud before approving it.",
  },
  RECIPE: {
    eyebrow: "Keep the recipes and their stories",
    label: "Cookbook",
    description:
      "Save the dish, ingredients, method, photos and family memories together. Check quantities, allergens and cooking instructions before approving each recipe.",
  },
  COMIC_CORE: {
    eyebrow: "Tell a story in pictures",
    label: "Comic book",
    description:
      "Bring a real moment to life in comic panels, captions and speech bubbles. Review the story, picture consistency and image permissions as you go.",
  },
};

type EditionPresentation = {
  family: EditionFamily;
  fits: PurchaserFit[];
  motif: EditionMotif;
  experienceLabel: string;
};

export const editionPresentation = {
  story: {
    family: "legacy",
    fits: ["parent", "self"],
    motif: "timeline",
    experienceLabel: "A whole life, shaped in chapters",
  },
  mother: {
    family: "legacy",
    fits: ["parent"],
    motif: "ribbon",
    experienceLabel: "Her whole self, beyond one role",
  },
  dad: {
    family: "legacy",
    fits: ["parent"],
    motif: "compass",
    experienceLabel: "The stories behind the steady hand",
  },
  ancestry: {
    family: "legacy",
    fits: ["family", "self"],
    motif: "map",
    experienceLabel: "Origins, crossings, names, and keepsakes",
  },
  love: {
    family: "relationships",
    fits: ["partner", "family", "self"],
    motif: "woven",
    experienceLabel: "Your life together, told by one of you",
  },
  lovestory: {
    family: "relationships",
    fits: ["partner", "self"],
    motif: "letters",
    experienceLabel: "One romance told through its turning points",
  },
  family: {
    family: "relationships",
    fits: ["family", "parent"],
    motif: "branches",
    experienceLabel: "Many contributors, one family-owned record",
  },
  children: {
    family: "playful",
    fits: ["parent", "family", "self"],
    motif: "moon",
    experienceLabel: "A true memory made gentle enough for bedtime",
  },
  action: {
    family: "playful",
    fits: ["parent", "partner", "self"],
    motif: "horizon",
    experienceLabel: "Real courage paced like an adventure",
  },
  comic: {
    family: "playful",
    fits: ["parent", "partner", "self"],
    motif: "panels",
    experienceLabel: "A true family origin story in sequential art",
  },
  holiday: {
    family: "traditions",
    fits: ["family", "parent", "self"],
    motif: "star",
    experienceLabel: "The imperfect traditions everyone retells",
  },
  recipes: {
    family: "traditions",
    fits: ["family", "parent", "self"],
    motif: "plate",
    experienceLabel: "The dish, the method, and the person behind it",
  },
} as const satisfies Record<TemplateId, EditionPresentation>;

export function editionFamily(templateId: string): EditionFamily {
  return editionPresentation[templateId as TemplateId]?.family ?? "legacy";
}

export function catalogAvailability(templateId: string): CatalogAvailability {
  const availability = bookCreationProfileForTemplate(templateId)?.availability;
  if (availability === "WORKING" || availability === "SANDBOX" || availability === "ROADMAP")
    return availability;
  return "ROADMAP";
}

/** A creation profile is not a price or a fulfillment entitlement. */
export function catalogOfferPresentation(templateId: string) {
  const voucherEligible = bookVoucherEligibleTemplates.some(({ id }) => id === templateId);
  return voucherEligible
    ? {
        voucherEligible: true,
        label: "Book voucher",
        value: BOOK_VOUCHER_PRICE_DISPLAY,
        description:
          "One private book, one printed copy and standard US shipping. Review and approve the print files before arranging delivery.",
      }
    : {
        voucherEligible: false,
        label: "Voucher eligibility",
        value: "Not voucher-eligible",
        description: "This edition is not included in the current book voucher.",
      };
}

export function catalogAction(templateId: TemplateId): {
  route: string;
  label: string;
  note: string;
} {
  // Every book begins from a redeemed voucher; a book started without one can't be finished,
  // so an edition no voucher covers shows its example instead of a start.
  if (bookVoucherStartsEdition(templateId))
    return {
      // The edition's own storyteller, so redemption starts on this edition and not another.
      route: `/redeem?edition=${templateId}${
        editionPresentation[templateId]?.fits[0]
          ? `&fit=${editionPresentation[templateId]!.fits[0]}`
          : ""
      }`,
      label: templateId === "children" ? "Start your bedtime book" : "Start this book",
      note: "Use your book voucher to begin. You’ll check every page before anything is printed.",
    };
  return {
    route: `/books/${templateId}#example`,
    label: "See the book example",
    note: "Explore the example while this edition is being prepared.",
  };
}

/** Physical dimensions come from the final reviewed print profile, not legacy template keys. */
export const catalogPrintDescription: Record<PrintFormat, string> = {
  "trade-bw-6x9": "Black-and-white interior",
  "trade-color-6x9": "Full-color interior",
  "trade-color-8.5x8.5": "Full-color interior",
  "picture-color-8.5x8.5": "Full-color picture book",
  "comic-color": "Full-color comic book",
};
